
import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingFallback } from '../common/LoadingFallback';
import { mobileDebugger } from '@/utils/mobileDebugger';

// Local development routes that can bypass auth
const DEV_MODE = process.env.NODE_ENV === 'development';
const DEV_BYPASS_ROUTES = [
  '/canvas',
];

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading, isInitialized, refreshSession, profile, isLocalDevMode } = useAuth();
  const location = useLocation();
  
  console.log('ProtectedRoute - State:', {
    user: user ? { id: user.id, email: user.email } : null,
    isLoading,
    isInitialized,
    pathname: location.pathname,
    timestamp: new Date().toISOString(),
    isAdmin: profile?.is_admin
  });

  // Log route changes for mobile debugging
  useEffect(() => {
    // Only log on mobile devices to avoid flooding the logs
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      mobileDebugger.logAuthEvent('route_change', {
        path: location.pathname,
        hasUser: !!user,
        isLoading,
        isInitialized
      });
    }
  }, [location.pathname, user, isLoading, isInitialized]);

  // Refresh session when entering a protected route, with standardized throttling
  useEffect(() => {
    // Only refresh if we have a user and auth is initialized
    if (user && !isLoading && isInitialized) {
      // Use timestamp to throttle refresh attempts
      const lastRefresh = localStorage.getItem(`route_refresh_${location.pathname}`);
      const now = Date.now();
      const MIN_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes (increased from 3 minutes)
      
      // Only refresh if we haven't refreshed recently on this route
      if (!lastRefresh || (now - parseInt(lastRefresh)) > MIN_REFRESH_INTERVAL) {
        console.log('ProtectedRoute - Validating session for route:', {
          pathname: location.pathname,
          userId: user.id,
          timestamp: new Date().toISOString(),
        });
        
        localStorage.setItem(`route_refresh_${location.pathname}`, now.toString());
        
        // Call refreshSession without parameters
        refreshSession()
          .then(() => {
            if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
              mobileDebugger.logAuthEvent('refresh_complete', { success: true });
            }
          })
          .catch(err => {
            console.error('Error refreshing session in ProtectedRoute:', err);
            if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
              mobileDebugger.logAuthEvent('session_error', { 
                error: String(err),
                errorType: err?.name,
                stack: err?.stack
              });
            }
          });
      } else {
        console.log('ProtectedRoute - Skipping refresh (recently refreshed):', {
          pathname: location.pathname,
          timeSinceLastRefresh: (now - parseInt(lastRefresh)) / 1000,
          timestamp: new Date().toISOString(),
        });
      }
    }
  }, [user, isLoading, isInitialized, refreshSession, location.pathname]);

  // Wait for auth to initialize
  if (!isInitialized) {
    console.log('ProtectedRoute - Waiting for initialization', {
      authInitialized: isInitialized,
      pathname: location.pathname,
      timestamp: new Date().toISOString(),
    });
    return <LoadingFallback />;
  }
  
  // Show loading state while auth is loading
  if (isLoading) {
    console.log('ProtectedRoute - Auth initialized but still loading data', {
      pathname: location.pathname,
      timestamp: new Date().toISOString(),
    });
    return <LoadingFallback />;
  }
  
  // Check if this route should bypass auth in dev mode
  const shouldBypassAuth = DEV_MODE && DEV_BYPASS_ROUTES.includes(location.pathname);
  
  // If in dev mode and on a bypass route, render children even without auth
  if (shouldBypassAuth) {
    console.log('⚡ DEV MODE: Bypassing auth for', location.pathname);
    return <>{children}</>;
  }
  
  // If not authenticated, redirect to login
  if (!user) {
    console.log('No authenticated user, redirecting to auth page', {
      pathname: location.pathname,
      timestamp: new Date().toISOString(),
    });
    return <Navigate to="/auth" replace state={{ from: location }} />;
  }
  
  // If all checks pass, render the protected content
  return <>{children}</>;
};
